<template>
  <div class="container">

    <HeadComponent/>

    <main class="row">
      <div class="col"></div>
      <div class="col-6">
        <h2>Create short link</h2>
        <form @submit.prevent="createShortLink">
          <div class="form-group">
            <input
                type="text"
                id="url"
                v-model="url"
                class="form-control"
                placeholder="New url"
                required
            />
          </div>
          <button type="submit" class="btn btn-success mt-2">Create short link</button>
        </form>
      </div>
      <div class="col"></div>
    </main>

    <FooterComponent/>
  </div>
</template>

<script>
import axios from "axios";

import HeadComponent from "@/components/views/head/HeadComponent.vue";
import FooterComponent from "@/components/views/footer/FooterComponent.vue";

const apiUrl = process.env.VUE_APP_API_URL;

export default {
  name: "CreateShortLinkComponent",
  /**
   * An object containing application components to be used within a Vue component.
   *
   * @property {Object} components - An object that defines the components used inside the current Vue component.
   * @property {Object} components.FooterComponent - The footer component used in the application.
   * @property {Object} components.HeadComponent - The header component used in the application.
   */
  components: {FooterComponent, HeadComponent},
  /**
   * Provides an object containing the initial states for URL, shortLink, and error.
   *
   * @return {Object} An object with the following properties:
   * - url: A string representing the URL, initialized as an empty string.
   * - shortLink: A string representing the shortened URL, initialized as an empty string.
   * - error: A string representing the error message, initialized as an empty string.
   */
  data() {
    return {
      url: "",
      shortLink: "",
      error: ""
    };
  },
  /**
   * Asynchronously creates a short link for the provided URL.
   * Validates the presence of token and user ID in session storage before making a request to the API endpoint.
   * On successful creation, displays a success message and redirects to the "Links" page.
   * In case of an error, displays an error message and sets the `error` property with the received or default error message.
   *
   * @async
   * @function createShortLink
   * @throws {Error} Displays error dialog with appropriate message if the API request fails or token/user ID is not found.
   */
  methods: {
    /**
     * Asynchronously creates a short link for the given original URL and associates it with the logged-in user.
     * Retrieves the user's token and ID from session storage and makes a POST request to the API.
     * Displays feedback to the user through confirmation or error dialogs.
     * Updates the `shortLink` or `error` properties based on the operation's success or failure.
     *
     * @return {Promise<void>} Resolves when the short link creation process completes. Does not explicitly return any value.
     */
    async createShortLink() {
      this.error = "";
      this.shortLink = "";
      const token = sessionStorage.getItem("token");
      const id = sessionStorage.getItem("id");
      if (!token || !id) {
        this.error = "Token or user ID not found";
        return;
      }
      try {
        const response = await axios.post(
            `${apiUrl}/createLinks`,
            {
              original_link: this.url,
              id_user: id
            },
            {
              headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`
              }
            }
        );

        if (response.data.ok) {
          await this.$swal.fire({
            icon: "success",
            title: "¡Short Link created!",
            text: response.data.message || "The short link has been created successfully.",
            confirmButtonText: "OK"
          });
          this.$router.push({name: "Links"});
        } else {
          await this.$swal.fire({
            icon: "error",
            title: "Error",
            text: response.data.error || "Unknown error.",
            confirmButtonText: "OK"
          });
          this.error = response.data.error || "Unknown error.";
        }
      } catch (e) {
        const errorMsg = e.response?.data?.error || "Network or server error.";
        await this.$swal.fire({
          icon: "error",
          title: "Error",
          text: errorMsg,
          confirmButtonText: "OK"
        });
        this.error = errorMsg;
      }
    }
  }
};
</script>

<style scoped>
.form-group {
  margin-bottom: 1rem;
}
</style>
